/**
 * Orb Manager Component
 *
 * Manages the lifecycle of animated orbs, spawning them when trials complete
 * and cleaning them up after animation finishes.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatedOrb } from "./AnimatedOrb";

export type GoldenTier = 'none' | 'personal' | 'overall';

interface OrbInstance {
  id: string;
  cardCenterY: number;
  studyId: string;
  goldenTier: GoldenTier;
}

interface OrbManagerProps {
  /**
   * Map of study IDs to their card center Y positions (relative to SVG)
   * Key: study_id, Value: cardCenterY
   */
  studyPositions: Map<string, number>;

  /**
   * Map of study IDs to their current trial numbers
   * When a trial number increments, spawn an orb for that study
   */
  trialCounts: Map<string, number>;

  /** Maximum concurrent orbs to prevent visual chaos */
  maxOrbs?: number;

  /** Optional color for orbs */
  color?: string;

  /**
   * Minimum time between orbs from the same study (in ms)
   * Prevents visual chaos during fast trial completion
   * Default: 3000ms (1 orb per 3 seconds per study)
   */
  minSpawnInterval?: number;

  /**
   * Demo mode: spawn orbs at random intervals for visual testing
   * Only enable during development
   */
  demoMode?: boolean;

  /**
   * Map of study IDs to their golden orb tier for their next spawn
   * 'personal' = beat own previous best (gold color, normal size)
   * 'overall' = beat the group's best (gold color, 1.6x size, slower)
   */
  goldenPending?: Map<string, 'personal' | 'overall'>;

  /**
   * Callback when a golden orb is spawned
   * Parent should clear the study from goldenPending
   */
  onGoldenConsumed?: (studyId: string) => void;
}

export function OrbManager({
  studyPositions,
  trialCounts,
  maxOrbs = 8,
  color,
  minSpawnInterval = 3000,
  demoMode = false,
  goldenPending,
  onGoldenConsumed,
}: OrbManagerProps) {
  const [activeOrbs, setActiveOrbs] = useState<OrbInstance[]>([]);

  // Track previous trial counts to detect increments
  const prevTrialCountsRef = useRef<Map<string, number>>(new Map());
  const orbIdCounter = useRef(0);

  // Track last spawn time per study to enforce rate limiting
  const lastSpawnTimeRef = useRef<Map<string, number>>(new Map());

  // Spawn an orb for a specific study (with rate limiting)
  const spawnOrb = useCallback(
    (studyId: string) => {
      const cardCenterY = studyPositions.get(studyId);
      if (cardCenterY === undefined) return;

      // Check cooldown - only spawn if enough time has passed since last orb for this study
      const now = Date.now();
      const lastSpawn = lastSpawnTimeRef.current.get(studyId) ?? 0;
      if (now - lastSpawn < minSpawnInterval) {
        return; // Skip - too soon since last orb for this study
      }

      // Update last spawn time
      lastSpawnTimeRef.current.set(studyId, now);

      // Check if this should be a golden orb and at what tier
      // 'personal' = beat own best, 'overall' = beat group's best
      const goldenTier: GoldenTier = goldenPending?.get(studyId) ?? 'none';

      const newOrb: OrbInstance = {
        id: `orb-${orbIdCounter.current++}`,
        cardCenterY,
        studyId,
        goldenTier,
      };

      // Notify parent that golden status was consumed
      if (goldenTier !== 'none' && onGoldenConsumed) {
        onGoldenConsumed(studyId);
      }

      setActiveOrbs((prev) => {
        // Limit concurrent orbs - remove oldest if at max
        const updated = [...prev, newOrb];
        if (updated.length > maxOrbs) {
          return updated.slice(-maxOrbs);
        }
        return updated;
      });
    },
    [studyPositions, maxOrbs, minSpawnInterval, goldenPending, onGoldenConsumed]
  );

  // Spawn an orb at a specific Y position (for demo mode)
  const spawnOrbAtPosition = useCallback(
    (cardCenterY: number) => {
      const newOrb: OrbInstance = {
        id: `orb-${orbIdCounter.current++}`,
        cardCenterY,
        studyId: `demo-${Date.now()}`,
        goldenTier: 'none',
      };

      setActiveOrbs((prev) => {
        const updated = [...prev, newOrb];
        if (updated.length > maxOrbs) {
          return updated.slice(-maxOrbs);
        }
        return updated;
      });
    },
    [maxOrbs]
  );

  // Remove orb when animation completes
  const handleOrbComplete = useCallback((orbId: string) => {
    setActiveOrbs((prev) => prev.filter((orb) => orb.id !== orbId));
  }, []);

  // Detect trial count changes and spawn orbs
  useEffect(() => {
    const prevCounts = prevTrialCountsRef.current;

    trialCounts.forEach((currentCount, studyId) => {
      // Only spawn orb if we were already tracking this study
      // This prevents orbs from spawning when a study first appears with an existing trial count
      if (!prevCounts.has(studyId)) {
        return; // Study just appeared - start tracking but don't spawn
      }

      const prevCount = prevCounts.get(studyId)!;

      // If trial count increased, attempt to spawn an orb
      // Rate limiting is handled in spawnOrb, so we just call it once per update
      if (currentCount > prevCount) {
        spawnOrb(studyId);
      }
    });

    // Update previous counts ref
    prevTrialCountsRef.current = new Map(trialCounts);
  }, [trialCounts, spawnOrb]);

  // Demo mode: spawn orbs at random intervals
  useEffect(() => {
    if (!demoMode) return;

    const positions = Array.from(studyPositions.values());
    if (positions.length === 0) return;

    const spawnRandomOrb = () => {
      const randomIndex = Math.floor(Math.random() * positions.length);
      const position = positions[randomIndex];
      if (position !== undefined) {
        spawnOrbAtPosition(position);
      }
    };

    // Spawn initial orb
    spawnRandomOrb();

    // Spawn more orbs at random intervals
    const interval = setInterval(() => {
      spawnRandomOrb();
    }, 800 + Math.random() * 400); // 800-1200ms between orbs

    return () => clearInterval(interval);
  }, [demoMode, studyPositions, spawnOrbAtPosition]);

  // Always render the group container (even if empty) so it's available in DOM
  // This helps with debugging and ensures consistent structure
  return (
    <g className="orb-manager">
      {activeOrbs.map((orb) => (
        <AnimatedOrb
          key={orb.id}
          orbKey={orb.id}
          cardCenterY={orb.cardCenterY}
          onComplete={() => handleOrbComplete(orb.id)}
          color={color}
          goldenTier={orb.goldenTier}
        />
      ))}
    </g>
  );
}

export default OrbManager;
